import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import {
  Check,
  X,
  Info,
  User,
  Plus,
  ChevronRight,
  ChevronLeft,
  FileText,
} from "lucide-react";
import config from "../../../config/config";
import {
  WIZARD_TOTAL_STEPS,
  formatCurrency,
  formatCurrencyDetailed,
  getPaketYenilemeNotu,
  getPaketOzellikleri,
  parseModulListesi,
  isEnterprisePaket,
  isOnlineBankaModul,
  isEFatura,
  isEIrsaliye,
  getModulFiyati,
  getModulGosterimFiyati,
  getOnlineBankaSecenekleri,
  getIskontoHaricToplam,
  getIskontoSonrasiPaketModul,
  getEkKullaniciSayisi,
  getBulutSunucuToplamFiyat,
  normTr,
} from "./fiyatWizardUtils";
import "./FiyatHesaplamaWizard.css";

const ICON = { size: 15, strokeWidth: 2.25 };

const WIZARD_STEP_DEFS = [
  { id: 1, label: "Paket" },
  { id: 2, label: "Modüller" },
  { id: 3, label: "Kullanıcı" },
  { id: 4, label: "Sunucu" },
  { id: 5, label: "İskonto" },
  { id: 6, label: "Özet" },
];

const StepHeader = ({ badge, title, subtitle, centered }) => (
  <div className={centered ? "fhw-step-header" : "fhw-step-header"} style={centered ? { textAlign: "center" } : undefined}>
    {badge && <span className="fhw-step-badge">{badge}</span>}
    <h2 className="fhw-title">{title}</h2>
    {subtitle && <p className="fhw-subtitle">{subtitle}</p>}
  </div>
);

const IconSlot = ({ children, className = "" }) => (
  <span className={`fhw-icon-slot ${className}`}>{children}</span>
);

const FeatureIcon = ({ type }) => {
  if (type === "info") {
    return (
      <IconSlot className="fhw-icon-slot--info">
        <Info {...ICON} />
      </IconSlot>
    );
  }
  if (type === "error") {
    return (
      <IconSlot className="fhw-icon-slot--error">
        <X {...ICON} />
      </IconSlot>
    );
  }
  if (type === "user") {
    return (
      <IconSlot className="fhw-icon-slot--user">
        <User {...ICON} />
      </IconSlot>
    );
  }
  return (
    <IconSlot className="fhw-icon-slot--check">
      <Check {...ICON} />
    </IconSlot>
  );
};

const ModulListIcon = ({ isIncluded }) => {
  if (isIncluded) {
    return (
      <IconSlot className="fhw-icon-slot--plus">
        <Plus size={14} strokeWidth={2.5} />
      </IconSlot>
    );
  }
  return (
    <IconSlot className="fhw-icon-slot--check">
      <Check size={14} strokeWidth={2.5} />
    </IconSlot>
  );
};

const FiyatHesaplamaWizard = ({
  token,
  paketler,
  fiyatlar,
  paketDetayList,
  selectedPaket,
  setSelectedPaket,
  selectedPaketData,
  setSelectedPaketData,
  availableModules,
  setAvailableModules,
  selectedModules,
  setSelectedModules,
  kullaniciSayisi,
  setKullaniciSayisi,
  setPaketIskontoOrani,
  setModulIskontoOrani,
  setEkKullaniciIskontoOrani,
  bulutSunucu,
  setBulutSunucu,
  onlineBankaPaket,
  setOnlineBankaPaket,
  hesaplamaSonucu,
  setHesaplamaSonucu,
  bayiKazanc,
  onTeklifOlustur,
  pdfIndirLoading,
  fetchPaketModulleri,
}) => {
  const [step, setStep] = useState(1);
  const [iskontoOrani, setIskontoOrani] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [paketModulDetay, setPaketModulDetay] = useState({});

  const onlineBankaSecili = Boolean(onlineBankaPaket);
  const shiftPaket = normTr(selectedPaketData?.Adi).includes("shift");

  const paketDetayMap = useMemo(() => {
    const map = {};
    (paketDetayList || []).forEach((p) => {
      map[p.PaketId] = p;
    });
    return map;
  }, [paketDetayList]);

  const getPaketFiyati = useCallback(
    (paketId) => fiyatlar?.Paket?.[paketId] ?? 0,
    [fiyatlar]
  );

  const loadPaketDetay = useCallback(
    async (paketId) => {
      if (!paketId) return;
      try {
        const res = await axios.get(
          `${config.baseURL}/paketler/paket-detay/${paketId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data) {
          setPaketModulDetay((prev) => {
            if (prev[paketId]) return prev;
            return { ...prev, [paketId]: res.data };
          });
        }
      } catch (e) {
        console.error("Paket detay:", e);
      }
    },
    [token]
  );

  useEffect(() => {
    paketler.forEach((p) => loadPaketDetay(p.Id));
  }, [paketler, loadPaketDetay]);

  const hesapla = useCallback(
    async (iskonto = iskontoOrani, silent = false) => {
      if (!selectedPaket) return null;
      try {
        if (!silent) setCalculating(true);
        else setPreviewLoading(true);

        const isk = Math.max(0, Math.min(30, Number(iskonto) || 0));
        const response = await axios.post(
          `${config.baseURL}/fiyatlar/hesapla`,
          {
            PaketId: parseInt(selectedPaket, 10),
            ModulIds: selectedModules.map((m) => m.Id),
            KullaniciSayisi: parseInt(kullaniciSayisi, 10),
            BulutSunucu: bulutSunucu,
            OnlineBankaPaket: onlineBankaPaket
              ? parseInt(onlineBankaPaket, 10)
              : null,
            IskontoPaket: isk,
            IskontoModul: isk,
            IskontoEkKullanici: isk,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          setHesaplamaSonucu(response.data.data);
          setPaketIskontoOrani(isk);
          setModulIskontoOrani(isk);
          setEkKullaniciIskontoOrani(isk);
          return response.data.data;
        }
      } catch (e) {
        console.error("Hesaplama:", e);
      } finally {
        setCalculating(false);
        setPreviewLoading(false);
      }
      return null;
    },
    [
      selectedPaket,
      selectedModules,
      kullaniciSayisi,
      bulutSunucu,
      onlineBankaPaket,
      iskontoOrani,
      token,
      setHesaplamaSonucu,
      setPaketIskontoOrani,
      setModulIskontoOrani,
      setEkKullaniciIskontoOrani,
    ]
  );

  useEffect(() => {
    if (!selectedPaket || step >= 6) return;
    const t = setTimeout(() => hesapla(iskontoOrani, true), 400);
    return () => clearTimeout(t);
  }, [
    selectedPaket,
    selectedModules,
    kullaniciSayisi,
    bulutSunucu,
    onlineBankaPaket,
    step,
    hesapla,
    iskontoOrani,
  ]);

  const handlePaketSec = async (paket) => {
    if (isEnterprisePaket(paket)) return;
    const id = String(paket.Id);
    setSelectedPaket(id);
    setSelectedPaketData(paket);
    setSelectedModules([]);
    setOnlineBankaPaket("");
    setBulutSunucu(false);
    setKullaniciSayisi(1);
    setIskontoOrani(0);
    setHesaplamaSonucu(null);
    await fetchPaketModulleri(id, paket);
    await loadPaketDetay(paket.Id);
  };

  const modulGridItems = useMemo(() => {
    const items = [...(availableModules || [])];
    if (!shiftPaket) {
      items.push({
        Id: "__online_banka__",
        ProgramAdi: "Online Banka Entegrasyonu",
        isOnlineBanka: true,
      });
    }
    return items;
  }, [availableModules, shiftPaket]);

  const toggleModul = (modul) => {
    if (modul.isOnlineBanka) {
      if (onlineBankaSecili) {
        setOnlineBankaPaket("");
      } else {
        setOnlineBankaPaket("1");
      }
      return;
    }
    const isSelected = selectedModules.some((m) => m.Id === modul.Id);
    if (isSelected) {
      setSelectedModules(selectedModules.filter((m) => m.Id !== modul.Id));
    } else {
      setSelectedModules([...selectedModules, modul]);
    }
  };

  const canGoNext = () => {
    if (step === 1) return Boolean(selectedPaket);
    if (step === 2) {
      if (onlineBankaSecili && !onlineBankaPaket) return false;
      return true;
    }
    if (step === 3) {
      const max = selectedPaketData?.MaxKullaniciSayisi;
      if (kullaniciSayisi < 1) return false;
      if (max && kullaniciSayisi > max) return false;
      return true;
    }
    if (step === 4) return true;
    if (step === 5) return true;
    return false;
  };

  const handleNext = async () => {
    if (!canGoNext()) return;

    if (step === 5) {
      await hesapla(iskontoOrani, false);
      setStep(6);
      return;
    }

    if (step === 1 && shiftPaket) {
      setStep(3);
      return;
    }

    if (step === 2 && shiftPaket) {
      setStep(4);
      return;
    }

    setStep((s) => Math.min(s + 1, WIZARD_TOTAL_STEPS));
  };

  const handleBack = () => {
    if (step === 6) {
      setStep(5);
      return;
    }
    if (step === 3 && shiftPaket) {
      setStep(1);
      return;
    }
    if (step === 4 && shiftPaket) {
      setStep(3);
      return;
    }
    setStep((s) => Math.max(s - 1, 1));
  };

  const sortedPaketler = useMemo(
    () =>
      [...(paketler || [])].sort((a, b) => {
        const sa = a.Sira != null ? Number(a.Sira) : 2147483647;
        const sb = b.Sira != null ? Number(b.Sira) : 2147483647;
        return sa - sb || (a.Id ?? 0) - (b.Id ?? 0);
      }),
    [paketler]
  );

  const renderPaketCard = (paket) => {
    const detay = paketDetayMap[paket.Id] || {};
    const modulDetay = paketModulDetay[paket.Id];
    const fiyat = getPaketFiyati(paket.Id);
    const enterprise = isEnterprisePaket(paket);
    const selected = String(selectedPaket) === String(paket.Id);
    const moduller =
      modulDetay?.ModulDetaylar?.map((m) => m.ModulAdi) ||
      parseModulListesi(detay.Programlar);
    const ozellikler = getPaketOzellikleri(paket, paket.MaxKullaniciSayisi);

    return (
      <div
        key={paket.Id}
        className={`fhw-paket-card ${selected ? "selected" : ""} ${enterprise ? "enterprise" : ""}`}
        onClick={() => !enterprise && handlePaketSec(paket)}
        role="button"
        tabIndex={enterprise ? -1 : 0}
        onKeyDown={(e) => e.key === "Enter" && !enterprise && handlePaketSec(paket)}
      >
        <div className="fhw-paket-card-header">
          <h3 className="fhw-paket-name">{paket.Adi}</h3>
          {!enterprise && (
            <label
              className="fhw-paket-select"
              onClick={(e) => e.stopPropagation()}
            >
              <span>{selected ? "Seçildi" : "Seç"}</span>
              <input type="radio" checked={selected} readOnly />
            </label>
          )}
        </div>
        <p className="fhw-paket-price">
          {enterprise ? (
            "Fiyat Sorunuz"
          ) : (
            <>
              {formatCurrency(fiyat)} <span>/ yıl</span>
            </>
          )}
        </p>
        <div className="fhw-renewal-box">{getPaketYenilemeNotu(paket.Adi)}</div>
        <div className="fhw-paket-card-grow">
        <ul className="fhw-feature-list">
          {ozellikler.map((f, i) => (
            <li key={i}>
              <FeatureIcon type={f.type} />
              <span className="fhw-feature-text">{f.text}</span>
            </li>
          ))}
        </ul>
        {moduller.length > 0 && (
          <>
            <hr className="fhw-modul-divider" />
            <ul className="fhw-modul-list">
              {moduller.map((m, i) => {
                const lower = normTr(m);
                const isIncluded =
                  lower.includes("paketi") ||
                  lower.includes("starter") ||
                  lower.includes("master") ||
                  lower.includes("fast");
                return (
                  <li key={i} className={isIncluded ? "included-paket" : ""}>
                    <ModulListIcon isIncluded={isIncluded} />
                    <span className="fhw-modul-list-text">{m}</span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
        </div>
        <div className="fhw-paket-card-footer">
          <Info size={14} strokeWidth={2} className="fhw-paket-info-icon" />
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <div>
          <StepHeader
            title="Pozitif Ofis ERP Paketler"
          />
          <div className="fhw-paket-grid">
            {sortedPaketler.map((p) => renderPaketCard(p))}
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div>
          <StepHeader
            badge="Adım 2"
            title="Ek Modül İster misiniz?"
            subtitle="İhtiyacınıza göre ek modüller seçebilirsiniz. Seçtikçe fiyata eklenir."
          />
          {modulGridItems.length === 0 ? (
            <p style={{ color: "#6b7280" }}>
              Bu paket için satın alınabilir ek modül bulunmamaktadır.
            </p>
          ) : (
            <div className="fhw-modul-grid">
              {modulGridItems.map((modul) => {
                const isOb = modul.isOnlineBanka;
                const selected = isOb
                  ? onlineBankaSecili
                  : selectedModules.some((m) => m.Id === modul.Id);
                const fiyat = isOb
                  ? null
                  : getModulGosterimFiyati(modul, selectedModules, fiyatlar);
                const isEFaturaOrIrsaliye =
                  !isOb && (isEFatura(modul) || isEIrsaliye(modul));
                const otherEFaturaIrsaliyeSelected =
                  isEFaturaOrIrsaliye &&
                  !selected &&
                  selectedModules.some(
                    (m) =>
                      m.Id !== modul.Id &&
                      (isEFatura(m) || isEIrsaliye(m))
                  );

                return (
                  <div
                    key={modul.Id}
                    className={`fhw-modul-card ${selected ? "selected" : ""}`}
                    onClick={() => toggleModul(modul)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && toggleModul(modul)}
                  >
                    <div className="fhw-modul-check">
                      {selected ? "✓" : ""}
                    </div>
                    <div className="fhw-modul-card-name">{modul.ProgramAdi}</div>
                    <div
                      className={`fhw-modul-card-price${
                        otherEFaturaIrsaliyeSelected
                          ? " fhw-modul-card-price--bundle"
                          : ""
                      }`}
                    >
                      {isOb
                        ? onlineBankaSecili
                          ? "Seçildi"
                          : "Miktar Seçiniz"
                        : formatCurrency(fiyat)}
                    </div>
                    {otherEFaturaIrsaliyeSelected && (
                      <div className="fhw-modul-card-hint">Birlikte paket fiyatı</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {onlineBankaSecili && (
            <div className="fhw-online-banka-panel">
              <h4>Online Banka Sayısı</h4>
              <p>
                Aynı banka içindeki tüm hesaplarınızı tek banka olarak
                entegre edebilirsiniz. Farklı banka sayısına göre paket seçin.
              </p>
              <div className="fhw-banka-grid">
                {getOnlineBankaSecenekleri(fiyatlar).map((opt) => (
                  <div
                    key={opt.value}
                    className={`fhw-banka-option ${
                      onlineBankaPaket === opt.value ? "selected" : ""
                    }`}
                    onClick={() => setOnlineBankaPaket(opt.value)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === "Enter" && setOnlineBankaPaket(opt.value)
                    }
                  >
                    <input
                      type="radio"
                      checked={onlineBankaPaket === opt.value}
                      readOnly
                    />
                    <div>
                      <div className="fhw-banka-option-label">{opt.label}</div>
                      <div className="fhw-banka-option-price">
                        {formatCurrencyDetailed(opt.fiyat)} / yıl
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (step === 3) {
      const max = selectedPaketData?.MaxKullaniciSayisi;
      return (
        <div className="fhw-step-centered">
          <StepHeader
            badge="Adım 3"
            title="Kullanıcı Sayısı Belirleyin"
            subtitle={`Aynı anda kaç kullanıcının Pozitif Ofis'e erişeceğini belirleyin.${max ? ` (Maksimum ${max})` : ""}`}
            centered
          />
          <input
            className="fhw-user-input"
            type="number"
            min={1}
            max={max || undefined}
            value={kullaniciSayisi}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10) || 1;
              if (max) {
                setKullaniciSayisi(Math.min(Math.max(1, v), max));
              } else {
                setKullaniciSayisi(Math.max(1, v));
              }
            }}
          />
        </div>
      );
    }

    if (step === 4) {
      const ekSay = getEkKullaniciSayisi(kullaniciSayisi);
      const bulutEkBirim = Number(fiyatlar?.BulutSunucuEkKullanici) || 0;
      const bulutToplamTahmin = getBulutSunucuToplamFiyat(fiyatlar, kullaniciSayisi);

      return (
        <div className="fhw-step-centered">
          <StepHeader
            badge="Adım 4"
            title="Sunucu Seçin"
            subtitle="Pozitif Ofis için yıllık bulut sunucu kiralayabilir veya kendi sunucunuza ücretsiz kurulum yapabilirsiniz."
            centered
          />
          <div className="fhw-server-options">
            <div
              className={`fhw-server-option ${!bulutSunucu ? "selected" : ""}`}
              onClick={() => setBulutSunucu(false)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setBulutSunucu(false)}
            >
              <input type="radio" checked={!bulutSunucu} readOnly />
              Yerel Sunucu
            </div>
            <div
              className={`fhw-server-option ${bulutSunucu ? "selected" : ""}`}
              onClick={() => setBulutSunucu(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setBulutSunucu(true)}
            >
              <input type="radio" checked={bulutSunucu} readOnly />
              <span className="fhw-server-option-text">
                <span>Bulut Sunucu</span>
                {ekSay > 0 && bulutEkBirim > 0 && (
                  <span className="fhw-server-option-detail">
                    +{ekSay} ek kullanıcı × {formatCurrency(bulutEkBirim)}
                  </span>
                )}
              </span>
              {bulutToplamTahmin > 0 && (
                <span className="fhw-server-option-price">
                  +{formatCurrency(bulutToplamTahmin)}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (step === 5) {
      return (
        <div className="fhw-step-centered">
          <StepHeader
            badge="Adım 5"
            title="İskonto Oranı"
            subtitle="İsteğe bağlı iskonto uygulayabilirsiniz (maksimum %30)."
            centered
          />
          <div className="fhw-discount-box">
            <div className="fhw-discount-input-wrap">
              <input
                className="fhw-discount-input"
                type="number"
                min={0}
                max={30}
                step={0.5}
                value={iskontoOrani}
                onChange={(e) =>
                  setIskontoOrani(
                    Math.max(0, Math.min(30, Number(e.target.value) || 0))
                  )
                }
              />
              <span style={{ fontSize: "1.25rem", fontWeight: 600 }}>%</span>
            </div>
            <div className="fhw-discount-hint">
              İskonto; paket, ek modül ve ek kullanıcı bedellerine uygulanır.
              Bulut sunucu, bulut sunucu ek kullanıcı ve online banka bedelleri
              iskonto dışındadır.
            </div>
          </div>
        </div>
      );
    }

    if (step === 6) {
      const d = hesaplamaSonucu?.hesaplamaDetaylari || {};
      const iskontosuzPaketModul = getIskontoHaricToplam(hesaplamaSonucu);
      const iskontoluPaketModul = getIskontoSonrasiPaketModul(hesaplamaSonucu);
      const hasEkKullanici =
        (d.ekKullaniciFiyatiOrijinal ?? d.ekKullaniciFiyati ?? 0) > 0;
      const iskontoKalemLabel = hasEkKullanici
        ? "Paket + Modül + Ek Kullanıcı"
        : "Paket + Modül";
      const bulutTaban = d.bulutSunucuFiyati ?? 0;
      const bulutEkToplam = d.bulutSunucuEkKullaniciFiyati ?? 0;
      const bulutEkSay = getEkKullaniciSayisi(kullaniciSayisi);
      const onlineBanka = d.onlineBankaFiyati ?? 0;
      const genelToplam = hesaplamaSonucu?.toplamFiyat ?? 0;

      return (
        <div className="fhw-step-centered fhw-step-centered--wide">
          <StepHeader
            badge="Adım 6"
            title="Hesaplama Özeti"
            subtitle="Teklif oluşturmak için aşağıdaki bilgileri kontrol edin."
            centered
          />

          <div className="fhw-summary-grid">
            <div className="fhw-summary-box">
              <div className="fhw-summary-box-label">
                İskontosuz {iskontoKalemLabel}
              </div>
              <div className="fhw-summary-box-value">
                {formatCurrencyDetailed(iskontosuzPaketModul)}
              </div>
            </div>
            <div className="fhw-summary-box highlight">
              <div className="fhw-summary-box-label">
                İskontolu {iskontoKalemLabel} ({iskontoOrani}%)
              </div>
              <div className="fhw-summary-box-value">
                {formatCurrencyDetailed(iskontoluPaketModul)}
              </div>
            </div>
            {bulutTaban > 0 && (
              <div className="fhw-summary-box">
                <div className="fhw-summary-box-label">Bulut Sunucu</div>
                <div className="fhw-summary-box-value">
                  {formatCurrencyDetailed(bulutTaban)}
                </div>
              </div>
            )}
            {bulutEkToplam > 0 && (
              <div className="fhw-summary-box">
                <div className="fhw-summary-box-label">
                  Bulut Sunucu Ek Kullanıcı ({bulutEkSay} adet)
                </div>
                <div className="fhw-summary-box-value">
                  {formatCurrencyDetailed(bulutEkToplam)}
                </div>
              </div>
            )}
            {onlineBanka > 0 && (
              <div className="fhw-summary-box">
                <div className="fhw-summary-box-label">Online Banka</div>
                <div className="fhw-summary-box-value">
                  {formatCurrencyDetailed(onlineBanka)}
                </div>
              </div>
            )}
            <div className="fhw-summary-box highlight">
              <div className="fhw-summary-box-label">Genel Toplam (KDV Dahil)</div>
              <div className="fhw-summary-box-value">
                {formatCurrencyDetailed(genelToplam)}
              </div>
            </div>
            {bayiKazanc && bayiKazanc.tier !== "genelMerkez" && (
              <>
                <div className="fhw-summary-box profit">
                  <div className="fhw-summary-box-label">Bayi Kazancı</div>
                  <div className="fhw-summary-box-value" style={{ color: "#3b82f6" }}>
                    {formatCurrencyDetailed(bayiKazanc.bayiKazanc ?? 0)}
                  </div>
                </div>
                <div className="fhw-summary-box payment">
                  <div className="fhw-summary-box-label">Bayinin Ödeyeceği</div>
                  <div className="fhw-summary-box-value" style={{ color: "#ef4444" }}>
                    {formatCurrencyDetailed(bayiKazanc.bayiOdeme ?? 0)}
                  </div>
                </div>
              </>
            )}
          </div>

          <button
            type="button"
            className="fhw-teklif-btn"
            onClick={onTeklifOlustur}
            disabled={!hesaplamaSonucu || pdfIndirLoading}
          >
            {pdfIndirLoading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <>
                <FileText size={20} /> Teklif Oluştur
              </>
            )}
          </button>
        </div>
      );
    }

    return null;
  };

  const secilenModulTags = useMemo(() => {
    const tags = selectedModules
      .filter((m) => !isOnlineBankaModul(m))
      .map((m) => `#${normTr(m.ProgramAdi).replace(/\s+/g, "")}`);
    if (onlineBankaSecili) tags.push("#onlineBanka");
    return tags;
  }, [selectedModules, onlineBankaSecili]);

  const displayTotal = shiftPaket ? WIZARD_TOTAL_STEPS - 1 : WIZARD_TOTAL_STEPS;
  const getDisplayStep = () => {
    if (step === 6) return displayTotal;
    if (shiftPaket) {
      const map = { 1: 1, 3: 2, 4: 3, 5: 4 };
      return map[step] ?? step;
    }
    return step;
  };

  const visibleSteps = useMemo(
    () =>
      shiftPaket
        ? WIZARD_STEP_DEFS.filter((s) => s.id !== 2)
        : WIZARD_STEP_DEFS,
    [shiftPaket]
  );

  const currentStepIndex = visibleSteps.findIndex((s) => s.id === step);

  return (
    <div className="fhw-shell">
      <nav className="fhw-progress" aria-label="Sihirbaz adımları">
        <div className="fhw-progress-track">
          {visibleSteps.map((s, idx) => {
            const isActive = s.id === step;
            const isDone = currentStepIndex >= 0 && idx < currentStepIndex;
            return (
              <div
                key={s.id}
                className={`fhw-progress-step${isActive ? " active" : ""}${isDone ? " done" : ""}`}
              >
                <div className="fhw-progress-dot">
                  {isDone ? "✓" : idx + 1}
                </div>
                <span className="fhw-progress-label">{s.label}</span>
              </div>
            );
          })}
        </div>
      </nav>

      <div className={`fhw-root${step === 1 ? " fhw-root--paket-step" : ""}`}>
        <div className={`fhw-main${step >= 3 && step <= 6 ? " fhw-main--centered" : ""}`}>
          <div className="fhw-main-panel">{renderStepContent()}</div>
        </div>

        <aside className="fhw-sidebar">
          <h3 className="fhw-sidebar-title">Paket Özeti</h3>
          <div className="fhw-sidebar-step">
            {getDisplayStep()}
            <small> / {displayTotal}</small>
          </div>

        {selectedPaketData ? (
          <div className="fhw-sidebar-paket">
            Pozitif Ofis {selectedPaketData.Adi}
          </div>
        ) : (
          <div className="fhw-sidebar-paket" style={{ color: "#9ca3af" }}>
            Paket seçiniz
          </div>
        )}

        {secilenModulTags.length > 0 && (
          <div className="fhw-sidebar-section">
            <div className="fhw-sidebar-section-title">
              {secilenModulTags.length} Ek Modül
            </div>
            <div className="fhw-tags">
              {secilenModulTags.map((t) => (
                <span key={t} className="fhw-tag">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {kullaniciSayisi > 0 && step >= 3 && (
          <div className="fhw-sidebar-info">
            {kullaniciSayisi} Kullanıcı
          </div>
        )}

        {step >= 4 && (
          <div className="fhw-sidebar-info">
            {bulutSunucu ? "Bulut Sunucu" : "Yerel Sunucu"}
          </div>
        )}

        {iskontoOrani > 0 && step >= 5 && (
          <div className="fhw-sidebar-info">İskonto: %{iskontoOrani}</div>
        )}

        <div className="fhw-sidebar-total">
          <div className="fhw-sidebar-total-label">Genel Toplam</div>
          {previewLoading ? (
            <CircularProgress size={28} sx={{ mt: 1, color: "#5eead4" }} />
          ) : (
            <>
              <div className="fhw-sidebar-total-value">
                {hesaplamaSonucu
                  ? formatCurrency(hesaplamaSonucu.toplamFiyat)
                  : selectedPaket
                  ? formatCurrency(getPaketFiyati(parseInt(selectedPaket, 10)))
                  : "—"}
              </div>
              <div className="fhw-sidebar-total-kdv">Kdv Dahil</div>
            </>
          )}
        </div>

        {step < 6 && (
          <div className="fhw-sidebar-nav">
            {step > 1 && (
              <button type="button" className="fhw-btn-back" onClick={handleBack}>
                <ChevronLeft size={22} strokeWidth={2} />
              </button>
            )}
            <button
              type="button"
              className="fhw-btn-next"
              onClick={handleNext}
              disabled={!canGoNext() || calculating}
            >
              {calculating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <>
                  {step === 5 ? "Devam Et" : "Sonraki"}
                  <ChevronRight size={20} strokeWidth={2} />
                </>
              )}
            </button>
          </div>
        )}

        {step === 6 && (
          <div className="fhw-sidebar-nav">
            <button type="button" className="fhw-btn-back" onClick={handleBack}>
              <ChevronLeft size={22} strokeWidth={2} />
            </button>
          </div>
        )}
      </aside>
      </div>
    </div>
  );
};

export default FiyatHesaplamaWizard;
